import { useMemo } from 'react';
// components
import { CopyButton } from '@newo/ui-kit';
import type {
    IGenerationAttempt,
    IGenerationObservability
} from '@newo/api/gateway';
import ButtonSpoiler from '@/modules/ActLogDrawer/components/Runtime/components/ButtonSpoiler';
import DateTime from '@/modules/DateTime/components/DateTime';
// utils
import { cn } from '@/utils';
import { DATE_FORMAT } from '@/const';
import {
    ARROW,
    SEPARATOR,
    formatAttempt,
    formatAttemptModels,
    formatCost,
    formatGenerationSummary,
    formatMs,
    formatProvider,
    formatProviderModels,
    formatRetries,
    formatTokens,
    groupByGeneration
} from '@/modules/ActLogDrawer/components/Runtime/components/GenerationDetails/GenerationDetails.utils.ts';

interface GenerationDetailsProps {
    className?: string;
    observability: IGenerationObservability;
}

const renderRow = (
    label: string,
    value: React.ReactNode,
    copyValue?: string
) => (
    <div className="flex gap-2">
        <span>{label}:</span>
        <span className="break-all text-gray-6">{value}</span>
        {copyValue ? <CopyButton value={copyValue} /> : undefined}
    </div>
);

const renderAttemptRows = (attempt: IGenerationAttempt) => (
    <>
        {renderRow('Attempt', formatAttempt(attempt))}
        {renderRow(
            'Time',
            <>
                {formatMs(attempt.time_ms)}
                {SEPARATOR}
                <DateTime
                    format={DATE_FORMAT.FULL_12H_MS}
                    value={attempt.started_at}
                />
                {ARROW}
                <DateTime
                    format={DATE_FORMAT.FULL_12H_MS}
                    value={attempt.ended_at}
                />
            </>
        )}
        {renderRow('Models', formatAttemptModels(attempt))}
        {renderRow('Tokens', formatTokens(attempt))}
        {renderRow('Cost', formatCost(attempt))}
        {renderRow(
            'Attempt ID',
            attempt.generation_attempt_id,
            attempt.generation_attempt_id
        )}
    </>
);

const GenerationDetails = ({
    className,
    observability
}: GenerationDetailsProps) => {
    const { action, provider, script_runner: scriptRunner } = observability;
    const groups = useMemo(
        () => groupByGeneration(observability),
        [observability]
    );
    return (
        <div className={cn('flex flex-col', className)}>
            {renderRow(
                'Action generation',
                formatMs(action.generation_time_ms)
            )}
            {renderRow('Provider', formatProvider(provider))}
            {renderRow(
                'Provider completion',
                formatMs(provider?.completion_time_ms)
            )}
            {renderRow(
                'Source stream',
                formatRetries(scriptRunner.retry_count)
            )}
            {renderRow('Model', formatProviderModels(provider))}
            {groups.length > 0 ? (
                <ButtonSpoiler
                    className="pt-2"
                    hideText="Hide Generation Attempts"
                    showText="Show Generation Attempts"
                >
                    <div className="flex flex-col gap-2 pl-4">
                        {renderRow(
                            'Action generation ID',
                            observability.action_generation_id,
                            observability.action_generation_id
                        )}
                        {groups.map((group) => (
                            <div key={group.id}>
                                {renderRow(
                                    'Generation',
                                    formatGenerationSummary(group.summary)
                                )}
                                {renderRow('Generation ID', group.id, group.id)}
                                {group.attempts.map((attempt) => (
                                    <div
                                        className="pl-4 pt-1"
                                        key={attempt.generation_attempt_id}
                                    >
                                        {renderAttemptRows(attempt)}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </ButtonSpoiler>
            ) : undefined}
        </div>
    );
};

export default GenerationDetails;
